import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { isElementInViewport } from '@boldpenguin/emperor-common';
import { SelectorWrapperService } from '@boldpenguin/emperor-services';
import { IApplicationFormQuestionSet } from '@boldpenguin/sdk';
import { interval, Observable } from 'rxjs';
import {
  distinctUntilKeyChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeWhile,
} from 'rxjs/operators';

export const autofocusId = 'question-set-header-autofocus';
export const appFormContainerAutofocusId = 'app-form-content-autofocus';

const noop = () => undefined;

@Injectable({
  providedIn: 'root',
})
export class HeaderFocusService {
  readonly maxAttempts = 15;
  constructor(
    protected readonly selectorWrapperService: SelectorWrapperService,
    @Inject(DOCUMENT) protected readonly document: Document,
    private ngZone: NgZone,
  ) {}

  public findQuestionSetHeader(): Observable<HTMLElement> {
    return this.selectorWrapperService.getCurrentActiveQuestionSet$().pipe(
      filter(Boolean),
      distinctUntilKeyChanged('question_set_id'),
      switchMap((questionSet: IApplicationFormQuestionSet) =>
        this.findHeaderElement(questionSet.question_set.name),
      ),
    );
  }

  public focusOnElement(headerElement: HTMLElement): Promise<void> {
    const formContainerElement = this.document.getElementById(
      appFormContainerAutofocusId,
    );

    if (!formContainerElement) {
      return Promise.resolve();
    }

    // Make sure the elements are in a focusable state
    return Promise.all(
      formContainerElement.getAnimations({ subtree: true }).map(
        a => a.finished.catch(noop), // Swallow error from animations getting canceled.
      ),
    ).then(() => {
      this.ngZone.run(() => {
        if (!isElementInViewport(headerElement)) {
          headerElement.scrollIntoView();
        }
        headerElement.focus();
      });
    });
  }

  protected findHeaderElement(name: string): Observable<HTMLElement> {
    const trimmedName = name.trim();
    return interval(100).pipe(
      startWith(0),
      take(this.maxAttempts),
      map(() => {
        const element = this.document.getElementById(autofocusId);
        if (element?.textContent?.trim() === trimmedName) {
          return element;
        }
      }),
      takeWhile((element: HTMLElement | null) => !element, true),
      filter(Boolean),
    );
  }
}
